import { Quote } from '@client/model/quotesModel';
import { FundingPolicyType } from '@shared';

const SECURIAN_MIN_DEATH_BENEFIT_CAP = 10000;
const SECURIAN_MIN_DEATH_BENEFIT_RATE = 0.10;

function calculateMinGuaranteedDeathBenefit(
  fundingPolicyType: number,
  scaledDeathBenefit: number,
  originalValue: number | undefined,
  scalingFactor: number,
): number | undefined {
  if (fundingPolicyType === FundingPolicyType.secureCareThree) {
    return Math.min(SECURIAN_MIN_DEATH_BENEFIT_CAP, scaledDeathBenefit * SECURIAN_MIN_DEATH_BENEFIT_RATE);
  }
  return originalValue ? originalValue * scalingFactor : undefined;
}

/**
 * Linearly adjusts a quote's parameters based on a new premium amount
 * All parameters scale proportionally with the premium change
 */
export const adjustQuoteForPremium = (
  originalQuote: Quote,
  newPremiumAmount: number
): Quote => {

  if (!originalQuote.premiumAmount || originalQuote.premiumAmount <= 0) {
    return originalQuote;
  }

  // Calculate the scaling factor
  const scalingFactor = newPremiumAmount / originalQuote.premiumAmount;
  const scaledDeathBenefit = originalQuote.deathBenefitInitial * scalingFactor;

  const adjustedQuote: Quote = {
    ...originalQuote,
    premiumAmount: newPremiumAmount,
    
    deathBenefitInitial: scaledDeathBenefit,
    deathBenefitMinimumGuaranteedIndividual: calculateMinGuaranteedDeathBenefit(
      originalQuote.fundingPolicyType,
      scaledDeathBenefit,
      originalQuote.deathBenefitMinimumGuaranteedIndividual,
      scalingFactor,
    ),
    deathBenefitMinimumGuaranteedJoint: originalQuote.deathBenefitMinimumGuaranteedJoint
      ? originalQuote.deathBenefitMinimumGuaranteedJoint * scalingFactor
      : undefined,
    
    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: originalQuote.ltcBenefitAnnualIndividual
      ? originalQuote.ltcBenefitAnnualIndividual * scalingFactor
      : undefined,
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,

    // Params that stay the same (months and max-min investment don't scale with premium)
    ltcBenefitDurationMonths: originalQuote.ltcBenefitDurationMonths,
    investmentMin: originalQuote.investmentMin,
    investmentMax: originalQuote.investmentMax,

    // Clear any calculated values that need to be recalculated
    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };

  return adjustedQuote;
};

/**
 * Adjusts all quotes in an array for a new premium amount
 */
export const adjustQuotesForPremium = (
  originalQuotes: Quote[],
  newPremiumAmount: number
): Quote[] => {
  return originalQuotes.map(quote => adjustQuoteForPremium(quote, newPremiumAmount));
};
